import { z } from 'zod';
import { IncidentHazardTypeSchema, IncidentStatusSchema } from './incident';

/**
 * Evacuation Order ↔ Incident / Community cross-link (#206).
 *
 * An evacuation order/alert (an `arcgis_public_features` polygon, category
 * `evacuation`, migration 0063) is authoritative who-must-move context; an
 * incident is the actionable object the operator works; a community is the W.1
 * keystone object. Today they live on three surfaces with no thread between them.
 *
 * THE JOIN IS SPATIAL — there is no shared raw source and no FK to add (the #185
 * idiom: link on what the data already encodes, migration-free). The spatial edge
 * is `evac-order polygon ∩ community geometry`; the incident edge is the EXISTING
 * community→incident linkage (a headcount / wellness check carrying an
 * `incident_id`, the same linkage the Evacuation Logistics surface §4.4 uses):
 *
 *     evac order  ──(ST_Intersects)──▶  community  ──(incident_id)──▶  incident
 *
 * So an evac order is connected to the incidents that own the communities its
 * polygon covers. These schemas are the compact read-model the server returns;
 * the client gates the contextual render on a non-empty result (design-council
 * PART IX — no COP clutter when no link exists).
 */

/** A linked evacuation order/alert surfaced on a community / incident panel. */
export const EvacOrderRefSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  /** 'order' | 'alert' | null — the register (drives label + colour pairing). */
  severity: z.string().nullable(),
  event_type: z.string().nullable(),
  population: z.number().nullable(),
  homes: z.number().nullable(),
  /** Centroid for the COP fly-to + select. */
  lat: z.number(),
  lon: z.number(),
});
export type EvacOrderRef = z.infer<typeof EvacOrderRefSchema>;

/** An incident connected to an evac order (via a community its polygon covers). */
export const LinkedIncidentRefSchema = z.object({
  id: z.string(),
  incident_number: z.string(),
  name: z.string(),
  hazard_type: IncidentHazardTypeSchema,
  status: IncidentStatusSchema,
  lat: z.number(),
  lon: z.number(),
  /** §4.30 — gates the Evacuation Logistics entry (data-driven, ≥1 linked). */
  linked_community_count: z.number().int().nonnegative(),
});
export type LinkedIncidentRef = z.infer<typeof LinkedIncidentRefSchema>;

/** A community whose geometry an evac order's polygon covers. */
export const LinkedCommunityRefSchema = z.object({
  id: z.string(),
  name: z.string(),
  lat: z.number(),
  lon: z.number(),
});
export type LinkedCommunityRef = z.infer<typeof LinkedCommunityRefSchema>;

/** Response of GET /api/arcgis-features/:id/links — the evac order → both sides. */
export const EvacOrderLinksSchema = z.object({
  incidents: z.array(LinkedIncidentRefSchema),
  communities: z.array(LinkedCommunityRefSchema),
});
export type EvacOrderLinks = z.infer<typeof EvacOrderLinksSchema>;

/** Response of GET /api/{communities,incidents}/:id/evacuation-orders. */
export const EntityEvacOrdersSchema = z.object({
  orders: z.array(EvacOrderRefSchema),
});
export type EntityEvacOrders = z.infer<typeof EntityEvacOrdersSchema>;
